import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { paymentsAPI, customersAPI, vendorsAPI, invoicesAPI, billsAPI, companyAPI, bankingAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { makeMoneyFormatter, docAmount } from '../../utils/currency';

export default function PaymentForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type') || 'customer';
  const [entities, setEntities] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [rates, setRates] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [baseCurrency, setBaseCurrency] = useState('USD');
  const [form, setForm] = useState({
    customer_id: '', vendor_id: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'Cash', amount: 0,
    currency: '', exchange_rate: 1,
    bank_account_id: '', deposit_to_account_id: '',
    reference: '', memo: '', applications: [],
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (type === 'customer') customersAPI.list({}).then(setEntities);
    else vendorsAPI.list({}).then(setEntities);
  }, [type]);

  useEffect(() => {
    Promise.all([
      companyAPI.current().catch(() => null),
      companyAPI.currencies().catch(() => []),
      companyAPI.exchangeRates().catch(() => []),
      bankingAPI.accounts().catch(() => []),
    ]).then(([company, currs, rateRows, bankAccts]) => {
      const base = company?.base_currency || 'USD';
      setBaseCurrency(base);
      setCurrencies(currs.length ? currs : [{ code: 'USD', name: 'US Dollar', symbol: '$' }]);
      setRates(rateRows || []);
      setBankAccounts(bankAccts || []);
      setForm(f => ({ ...f, currency: f.currency || base }));
    });
  }, []);

  useEffect(() => {
    if (!form.currency || form.currency === baseCurrency) { setForm(f => ({ ...f, exchange_rate: 1 })); return; }
    const latest = rates.find(r => r.from_currency === form.currency && r.to_currency === baseCurrency);
    setForm(f => ({ ...f, exchange_rate: latest ? parseFloat(latest.rate) : f.exchange_rate }));
  }, [form.currency, baseCurrency, rates]);

  // Auto-select the bank account matching the chosen currency (AFN vs USD accounts)
  useEffect(() => {
    if (!form.currency) return;
    const field = type === 'customer' ? 'deposit_to_account_id' : 'bank_account_id';
    if (form[field]) return;
    const match = bankAccounts.find(a => (a.currency || baseCurrency) === form.currency);
    if (match) setForm(f => ({ ...f, [field]: match.id }));
  }, [form.currency, bankAccounts, baseCurrency, type]);

  const loadInvoices = async (entityId) => {
    if (!entityId) { setInvoices([]); return; }
    try {
      const rows = type === 'customer'
        ? await invoicesAPI.list({ customerId: entityId })
        : await billsAPI.list({ vendorId: entityId });
      // Only posted documents can be settled — a draft invoice/bill has not
      // been recorded in the ledger yet, so applying a payment to it would
      // corrupt the party balance and the accounts-payable/receivable control.
      setInvoices(rows.filter(d => parseFloat(d.amount_due || 0) > 0 && !['Draft', 'Voided'].includes(d.status)));
    } catch (err) { toast.error(err.message); setInvoices([]); }
  };

  const addApplication = (inv) => {
    // amount_applied is expressed in the payment's currency, so a document
    // recorded in another currency cannot be settled by this payment.
    if (docInfo(inv).currency !== (form.currency || baseCurrency)) {
      toast.error(`${type === 'customer' ? 'Invoice' : 'Bill'} is in ${docInfo(inv).currency} — change the payment currency to ${docInfo(inv).currency} to apply it.`);
      return;
    }
    const existing = form.applications.find(a => (type === 'customer' ? a.invoice_id : a.bill_id) === inv.id);
    if (existing) return;
    const maxApply = Math.min(
      parseFloat(inv.amount_due || inv.total),
      parseFloat(form.amount || 0) - form.applications.reduce((s, a) => s + a.amount_applied, 0)
    );
    if (maxApply <= 0) return;
    setForm({
      ...form,
      applications: [...form.applications, type === 'customer'
        ? { invoice_id: inv.id, amount_applied: maxApply }
        : { bill_id: inv.id, amount_applied: maxApply }]
    });
  };

  const removeApplication = (idx) => {
    setForm({ ...form, applications: form.applications.filter((_, i) => i !== idx) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const bankField = type === 'customer' ? 'deposit_to_account_id' : 'bank_account_id';
      const bankId = form[bankField];
      const ba = bankAccounts.find(a => String(a.id) === String(bankId));
      const payload = { ...form };
      payload[bankField] = ba ? ba.account_id : '';
      if (type === 'customer') payload.customer_id = form.customer_id;
      else payload.vendor_id = form.vendor_id;
      if (type === 'customer') await paymentsAPI.customerCreate(payload);
      else await paymentsAPI.vendorCreate(payload);
      toast.success('Payment recorded!');
      navigate('/payments');
    } catch (err) { toast.error(err.message); }
    setLoading(false);
  };

  const totalApplied = form.applications.reduce((s, a) => s + a.amount_applied, 0);
  const unapplied = (parseFloat(form.amount) || 0) - totalApplied;
  const entitySelected = type === 'customer' ? form.customer_id : form.vendor_id;
  const isForeign = form.currency && form.currency !== baseCurrency;
  const fxRate = parseFloat(form.exchange_rate) || 1;
  const baseAmount = (parseFloat(form.amount) || 0) * (isForeign ? fxRate : 1);

  const fmtBase = makeMoneyFormatter(baseCurrency);
  const fmtPay = makeMoneyFormatter(form.currency || baseCurrency);
  const docInfo = (row) => docAmount(
    { ...row, amount: row.amount_due !== undefined && row.amount_due !== null ? row.amount_due : row.total },
    baseCurrency
  );
  const fmtDoc = (row) => makeMoneyFormatter(docInfo(row).currency);

  // Outstanding documents are kept in their own currencies, so the total due is
  // reported per currency plus one base-currency figure — never a single sum
  // that would add AFN to USD.
  const dueByCurrency = invoices.reduce((acc, d) => {
    const info = docInfo(d);
    const key = info.currency;
    if (!acc[key]) acc[key] = { currency: key, total: 0, baseTotal: 0, rate: info.rate };
    acc[key].total += info.amount;
    acc[key].baseTotal += info.baseAmount;
    return acc;
  }, {});
  const dueList = Object.values(dueByCurrency).sort((a, b) => b.baseTotal - a.baseTotal);
  const totalDueBase = dueList.reduce((s, d) => s + d.baseTotal, 0);

  return (
    <div>
      <div className="toolbar">
        <h2>New {type === 'customer' ? 'Customer' : 'Vendor'} Payment</h2>
        <div className="toolbar-right">
          <button type="button" className="btn btn-outline" onClick={() => navigate('/payments')}>Cancel</button>
          <button type="submit" className="btn btn-primary" form="payment-form" disabled={loading}>
            {loading ? 'Saving...' : 'Record Payment'}
          </button>
        </div>
      </div>

      <form id="payment-form" onSubmit={handleSubmit}>
        {/* Payment Details */}
        <div className="df-editor" style={{ marginBottom: 12 }}>
          <div className="df-section-header">
            <span>Payment Details</span>
          </div>
          <div className="df-body">
            <div className="df-grid cols-3">
              <div className="df-field">
                <label>{type === 'customer' ? 'Customer' : 'Vendor'} <span className="req">*</span></label>
                <select required value={type === 'customer' ? form.customer_id : form.vendor_id}
                  onChange={e => {
                    setForm({ ...form, [type === 'customer' ? 'customer_id' : 'vendor_id']: e.target.value });
                    loadInvoices(e.target.value);
                  }}>
                  <option value="">Select...</option>
                  {entities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
              <div className="df-field">
                <label>Payment Date <span className="req">*</span></label>
                <input type="date" required value={form.payment_date} onChange={e => setForm({ ...form, payment_date: e.target.value })} />
              </div>
              <div className="df-field">
                <label>Amount <span className="req">*</span></label>
                <input type="number" step="0.01" min="0" required value={form.amount || ''} onChange={e => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} placeholder="0.00" />
              </div>
              <div className="df-field">
                <label>Payment Method</label>
                <select value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })}>
                  <option value="Cash">Cash</option>
                  <option value="Check">Check</option>
                  <option value="CreditCard">Credit Card</option>
                  <option value="BankTransfer">Bank Transfer</option>
                </select>
              </div>
              <div className="df-field">
                <label>Currency</label>
                <select value={form.currency || baseCurrency} onChange={e => setForm({ ...form, currency: e.target.value, [type === 'customer' ? 'deposit_to_account_id' : 'bank_account_id']: '' })}>
                  {currencies.map(c => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
                </select>
              </div>
              <div className="df-field">
                <label>Exchange Rate <span style={{ fontWeight: 400, color: '#94a3b8' }}>(1 {form.currency || baseCurrency} = ? {baseCurrency})</span></label>
                {isForeign ? (
                  <input type="number" step="0.0001" min="0.0001" value={form.exchange_rate || ''} placeholder="e.g. 70.5" onChange={e => setForm({ ...form, exchange_rate: parseFloat(e.target.value) || 1 })} />
                ) : (
                  <input value="1.0000" disabled />
                )}
              </div>
              <div className="df-field">
                <label>Bank Account</label>
                <select value={type === 'customer' ? form.deposit_to_account_id : form.bank_account_id}
                  onChange={e => setForm({ ...form, [type === 'customer' ? 'deposit_to_account_id' : 'bank_account_id']: e.target.value })}>
                  <option value="">Select...</option>
                  {bankAccounts.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.account_name || a.bank_name} — {a.currency || baseCurrency}
                      {(a.currency || baseCurrency) === form.currency ? ' (matches currency)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="df-field">
                <label>Reference</label>
                <input value={form.reference || ''} onChange={e => setForm({ ...form, reference: e.target.value })} placeholder="Optional" />
              </div>
              <div className="df-field">
                <label>Memo</label>
                <input value={form.memo || ''} onChange={e => setForm({ ...form, memo: e.target.value })} placeholder="Optional" />
              </div>
            </div>

            {isForeign && baseAmount > 0 && (
              <div style={{ marginTop: 12, padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: 13, color: '#166534' }}>
                <strong>{fmtPay(form.amount || 0)}</strong> = <strong>{fmtBase(baseAmount)}</strong>
                {' '}at 1 {form.currency} = {fxRate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} {baseCurrency}
              </div>
            )}
          </div>
        </div>

        {entitySelected && (
          <div className="df-editor" style={{ marginBottom: 12 }}>
            <div className="df-section-header">
              <span>Total Due Balance</span>
            </div>
            <div className="df-body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, color: '#475569' }}>
                {type === 'customer'
                  ? 'All outstanding invoices for this customer'
                  : 'All outstanding bills for this vendor'}
                {invoices.length > 0 ? ` (${invoices.length})` : ''}
              </span>
              <div style={{ textAlign: 'right' }}>
                {dueList.length === 0 ? (
                  <span style={{ fontSize: 18, fontWeight: 700, color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>—</span>
                ) : (
                  <>
                    <div style={{ fontSize: 18, fontWeight: 700, color: type === 'customer' ? '#0f766e' : '#b91c1c', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtBase(totalDueBase)}
                    </div>
                    {dueList.map(d => (
                      <div key={d.currency} style={{ fontSize: 12, color: '#475569', fontVariantNumeric: 'tabular-nums' }}>
                        {makeMoneyFormatter(d.currency)(d.total)}
                        {d.currency !== baseCurrency && ` = ${fmtBase(d.baseTotal)}`}
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Apply to Invoices/Bills */}
        {invoices.length > 0 && (
          <div className="df-editor" style={{ marginBottom: 12 }}>
            <div className="df-section-header">
              <span>Apply to {type === 'customer' ? 'Invoices' : 'Bills'} (Outstanding)</span>
              {form.applications.length > 0 && (
                <span style={{ fontWeight: 400, fontSize: 11, textTransform: 'none', letterSpacing: 0 }}>
                  Applied: {fmtPay(totalApplied)} &nbsp;|&nbsp; Unapplied: {fmtPay(unapplied)}
                </span>
              )}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '6px 10px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.3px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>#</th>
                    <th style={{ textAlign: 'left', padding: '6px 10px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.3px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>Date</th>
                    <th style={{ textAlign: 'right', padding: '6px 10px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.3px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>Due</th>
                    <th style={{ textAlign: 'right', padding: '6px 10px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.3px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>Balance</th>
                    <th style={{ padding: '6px 10px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.3px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(inv => {
                    const applied = form.applications.find(a => (type === 'customer' ? a.invoice_id : a.bill_id) === inv.id);
                    return (
                      <tr key={inv.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '6px 10px', fontSize: 12 }}>{type === 'customer' ? inv.invoice_number : inv.bill_number}</td>
                        <td style={{ padding: '6px 10px', fontSize: 12 }}>{type === 'customer' ? inv.invoice_date : inv.bill_date}</td>
                        <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right' }}>{inv.due_date}</td>
                        <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {fmtDoc(inv)(inv.amount_due || inv.total)}
                          {docInfo(inv).isForeign && (
                            <div style={{ fontSize: 10, color: '#0f766e' }}>= {fmtBase(docInfo(inv).baseAmount)}</div>
                          )}
                        </td>
                        <td style={{ padding: '6px 10px', fontSize: 12 }}>
                          {applied ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: 3, fontSize: 11, fontWeight: 600 }}>
                              Applied: {fmtPay(applied.amount_applied)}
                              <button type="button" onClick={() => removeApplication(form.applications.indexOf(applied))} style={{ background: 'none', border: 'none', color: '#166534', cursor: 'pointer', padding: 0, fontSize: 13, lineHeight: 1 }}>✕</button>
                            </span>
                          ) : (() => {
                            const info = docInfo(inv);
                            const mismatch = info.currency !== (form.currency || baseCurrency);
                            const docLabel = type === 'customer' ? 'Invoice' : 'Bill';
                            return (
                              <button
                                type="button"
                                className="btn btn-outline"
                                style={{
                                  padding: '2px 8px', fontSize: 11, height: 22,
                                  opacity: mismatch ? 0.5 : 1, cursor: mismatch ? 'not-allowed' : 'pointer',
                                }}
                                disabled={mismatch}
                                title={mismatch
                                  ? `${docLabel} is in ${info.currency}; switch the payment to ${info.currency} to apply it`
                                  : 'Apply'}
                                onClick={() => addApplication(inv)}>
                                Apply
                              </button>
                            );
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="df-actions">
          <button type="button" className="btn btn-outline" onClick={() => navigate('/payments')}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Saving...' : 'Record Payment'}</button>
        </div>
      </form>
    </div>
  );
}